import { Alert, Autocomplete, Box, Button, Stack, TextField, Typography } from '@mui/material';
import config from 'app/config';
import http from 'app/services/http';
import { putUserChecklistParkScope } from 'app/store/user';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

async function loadTransportsForGroups(groups) {
  if (!groups.length) {
    return [];
  }
  const chunks = await Promise.all(groups.map((g) => http.get(`/checklist/park-scope/transport-group/${g.id}/transports`)));
  const map = new Map();
  chunks.forEach((r) => {
    (r.data?.transports || []).forEach((t) => {
      map.set(t.id, t);
    });
  });
  return [...map.values()];
}

const UserChecklistParkScope = () => {
  const dispatch = useDispatch();
  const { selectedItem } = useSelector((state) => state.user);
  const userId = selectedItem?.id;

  const [groupOptions, setGroupOptions] = useState([]);
  const [transportOptions, setTransportOptions] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedTransports, setSelectedTransports] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!userId) {
        return;
      }
      setLoadError(null);
      try {
        const [{ data: groups }, { data: scope }] = await Promise.all([
          http.get('/checklist/park-scope/transport-groups'),
          http.get(`/user/${userId}/checklist-park-scope`),
        ]);
        if (cancelled) {
          return;
        }
        const gList = Array.isArray(groups) ? groups : [];
        setGroupOptions(gList);
        const gidSet = new Set(scope.transport_group_ids || []);
        const selG = gList.filter((g) => gidSet.has(g.id));
        setSelectedGroups(selG);
        const transports = await loadTransportsForGroups(selG);
        if (cancelled) {
          return;
        }
        setTransportOptions(transports);
        const tidSet = new Set(scope.transport_ids || []);
        const picked = transports.filter((t) => tidSet.has(t.id));
        (scope.transport_ids || []).forEach((id) => {
          if (!picked.some((t) => t.id === id)) {
            picked.push({ id, number: null, name: `ТС #${id}` });
          }
        });
        setSelectedTransports(picked);
      } catch {
        if (!cancelled) {
          setLoadError('Не удалось загрузить данные');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const handleGroupsChange = useCallback(async (_, newGroups) => {
    setSelectedGroups(newGroups);
    if (!newGroups.length) {
      setTransportOptions([]);
      setSelectedTransports([]);
      return;
    }
    try {
      const transports = await loadTransportsForGroups(newGroups);
      setTransportOptions(transports);
      setSelectedTransports((prev) => prev.filter((t) => transports.some((o) => o.id === t.id)));
    } catch {
      setTransportOptions([]);
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!userId) {
      return;
    }
    setSaveError(null);
    setSaving(true);
    try {
      await dispatch(
        putUserChecklistParkScope(userId, {
          transport_group_ids: selectedGroups.map((g) => g.id),
          transport_ids: selectedTransports.map((t) => t.id),
        })
      );
    } catch {
      setSaveError('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  }, [dispatch, userId, selectedGroups, selectedTransports]);

  if (!userId) {
    return null;
  }

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Typography variant='body2' color='text.secondary'>
        {config.clientUi === 'voykan'
          ? 'Пустые списки — на мобилке только «Направления осмотров». Если выбраны конкретные ТС ниже — на мобилке только они. Если отмечен только цех (без ТС) — все бригады этого цеха.'
          : 'Пустые списки — без ограничения по цеху/ТС (как раньше, только автоколонны). Если заданы цеха или ТС — доступ пересекается с направлениями (автоколоннами). Конкретные ТС в списке ниже имеют приоритет над цехом целиком.'}
      </Typography>
      {loadError && <Alert severity='error'>{loadError}</Alert>}
      {saveError && <Alert severity='error'>{saveError}</Alert>}
      <Autocomplete
        multiple
        options={groupOptions}
        getOptionLabel={(o) => o.name || String(o.id)}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        value={selectedGroups}
        onChange={handleGroupsChange}
        renderInput={(params) => <TextField {...params} label='Цеха (transport_group)' />}
      />
      <Autocomplete
        multiple
        options={transportOptions}
        getOptionLabel={(o) => o.number || o.name || `id ${o.id}`}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        value={selectedTransports}
        onChange={(_, v) => setSelectedTransports(v)}
        renderInput={(params) => <TextField {...params} label='Бригады / ТС (точечно)' />}
      />
      <Box>
        <Button variant='contained' onClick={handleSave} disabled={saving}>
          Сохранить область осмотров
        </Button>
      </Box>
    </Stack>
  );
};

export default UserChecklistParkScope;
